import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import toast from 'react-hot-toast';
import { useAddFollowUp } from '../../hooks/useStudentRecords';
import useTheme from '../../context/ThemeContext';

function AddFollowUpModal({ isOpen, onClose, recordId, onSuccess }) {
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});
  const currentTheme = useTheme()?.currentTheme;
  const { mutate: addFollowUp, isPending } = useAddFollowUp();

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (!note || note.trim().length < 5) {
      setErrors({ note: 'Follow-up note must be at least 5 characters' });
      return;
    }

    addFollowUp(
      { recordId, data: { note: note.trim() } },
      {
        onSuccess: () => {
          toast.success('Follow-up added successfully!');
          onClose();
          setNote('');
          if (onSuccess) onSuccess();
        },
        onError: (err) => {
          toast.error(err.response?.data?.message || 'Failed to add follow-up');
        },
      }
    );
  };

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary text-lg">Add Follow-up Note</h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors disabled:opacity-50"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Follow-up Note *
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isPending}
              placeholder="Add additional notes or updates..."
              maxLength={2000}
              rows={6}
              className={`w-full bg-card border rounded-xl px-3 py-2.5 text-sm text-secondary placeholder-subtle focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-secondary resize-none ${
                errors.note ? 'border-red-500' : 'border-divider'
              }`}
            />
            {errors.note && <p className="text-xs text-red-500 mt-1">{errors.note}</p>}
            <p className="text-xs text-muted mt-1">{note.length}/2000</p>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center gap-3 border-t border-divider pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1 px-4 py-2.5 bg-card border border-divider rounded-full text-secondary font-medium hover:bg-nav-hover transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className={`flex-1 px-4 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} font-medium rounded-full hover:opacity-90 transition-all duration-200 disabled:opacity-50`}
            >
              {isPending ? 'Adding...' : 'Add Follow-up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddFollowUpModal;
